"use client";

import clsx from "clsx";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/components/library/Icon";
import {
  MARKETPLACE_FILTER_FEATURED,
  type MarketplaceFilter,
  UnifiedIntegration,
} from "@/types/integrations";
import IntegrationCard from "./IntegrationCard";
import RequestCard from "./RequestCard";

const SKELETON_COUNT = 9;

function SkeletonCard() {
  return (
    <div
      className={clsx(
        "flex flex-col p-4 bg-white dark:bg-gray-800 rounded-xl",
        "border border-gray-100 dark:border-gray-700/50",
        "animate-pulse"
      )}
    >
      <div className="w-11 h-11 rounded-xl bg-gray-200 dark:bg-gray-700 mb-3" />
      <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
      <div className="h-3 w-full bg-gray-100 dark:bg-gray-700/60 rounded mb-1" />
      <div className="h-3 w-2/3 bg-gray-100 dark:bg-gray-700/60 rounded" />
    </div>
  );
}

function EmptyState({
  isFeatured,
  onRequestNew,
}: Readonly<{ isFeatured: boolean; onRequestNew: () => void }>) {
  const { t } = useTranslation("partners");

  return (
    <div className="flex flex-col items-center justify-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
      <Icon icon="magnifying-glass" className="w-10 h-10 text-gray-400 dark:text-gray-500 mb-3" />
      <p className="text-gray-600 dark:text-gray-400 font-medium mb-1">
        {t("marketplaceNoIntegrationsFound")}
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-500 mb-4 text-center max-w-md">
        {isFeatured ? t("marketplaceNoFeaturedAvailable") : t("marketplaceNoCategoryYet")}
      </p>
      <button
        onClick={onRequestNew}
        className="text-gailBlue font-medium hover:underline flex items-center gap-1"
      >
        <Icon icon="plus" />
        {t("marketplaceRequestFeature")}
      </button>
    </div>
  );
}

function GridCards(
  props: Readonly<{
    displayedIntegrations: UnifiedIntegration[];
    isFeatured: boolean;
    specialCards: ReactNode[];
    highlightedIntegrationId?: string | null;
    onConnect: (i: UnifiedIntegration) => void;
    onComingSoon: (i: UnifiedIntegration) => void;
    onDisconnect: (i: UnifiedIntegration) => void;
    onRequestNew: () => void;
  }>
) {
  if (props.displayedIntegrations.length === 0 && props.specialCards.length === 0) {
    return <EmptyState isFeatured={props.isFeatured} onRequestNew={props.onRequestNew} />;
  }

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
      {props.specialCards}
      {props.displayedIntegrations.map((i) => (
        <IntegrationCard
          key={i.id}
          integration={i}
          highlighted={props.highlightedIntegrationId === i.id}
          onConnect={() => props.onConnect(i)}
          onComingSoon={() => props.onComingSoon(i)}
          onDisconnect={() => props.onDisconnect(i)}
        />
      ))}
      {props.isFeatured ? null : <RequestCard onClick={props.onRequestNew} />}
    </div>
  );
}

export default function GridContent(
  props: Readonly<{
    selectedCategory: MarketplaceFilter;
    displayedIntegrations: UnifiedIntegration[];
    gridTitle: string;
    isLoading: boolean;
    /** Cards that are not backed by a catalog listing, rendered ahead of the grid. */
    specialCards: ReactNode[];
    highlightedIntegrationId?: string | null;
    onConnect: (i: UnifiedIntegration) => void;
    onComingSoon: (i: UnifiedIntegration) => void;
    onDisconnect: (i: UnifiedIntegration) => void;
    onRequestNew: () => void;
  }>
) {
  const { t } = useTranslation("partners");
  const isFeatured = props.selectedCategory === MARKETPLACE_FILTER_FEATURED;

  return (
    <main className="flex-1 min-w-0">
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{props.gridTitle}</h2>
        {isFeatured ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("marketplaceFeaturedSubtitle")}
          </p>
        ) : null}
      </div>

      {props.isLoading ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <SkeletonCard key={`skeleton-${i}`} />
          ))}
        </div>
      ) : (
        <GridCards
          displayedIntegrations={props.displayedIntegrations}
          isFeatured={isFeatured}
          specialCards={props.specialCards}
          highlightedIntegrationId={props.highlightedIntegrationId}
          onConnect={props.onConnect}
          onComingSoon={props.onComingSoon}
          onDisconnect={props.onDisconnect}
          onRequestNew={props.onRequestNew}
        />
      )}
    </main>
  );
}
